import { Box, Divider } from '@mui/material';
import { TournamentPlayerRole, TournamentPlayerStatus } from '@okletsplay/core';
import { DialogName } from 'app/enums';
import { TournamentTeamEntity } from 'app/interfaces/entity';
import { modalsReducers } from 'app/store/modal/slice';
import { ManageTournamentTeamActions } from 'modules/tournaments/components/BracketTournament/TournamentTeams/ManageTournamentTeamActions';
import React, { FC, Fragment, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { EmptyUserCard } from 'shared/components/EmptyUserCard';
import { TournamentTeamCardHeader } from '../TournamentTeamCardHeader';
import { TournamentTeamPlayerCard } from '../TournamentTeamPlayerCard';
import { useStyles } from './TournamentTeamCard.styles';

interface TournamentTeamCardProps {
  team: TournamentTeamEntity;
  teamsSize: number;
}

export const TournamentTeamCard: FC<TournamentTeamCardProps> = ({ team, teamsSize = 0 }) => {
  const dispatch = useDispatch();
  const { classes } = useStyles();

  const emptyPlayers = useMemo(() => {
    const playersToInviteCount = teamsSize - team?.players?.length ?? 0;
    if (!teamsSize || playersToInviteCount < 0) {
      return [];
    }

    return new Array(playersToInviteCount).fill(0);
  }, [team?.players?.length, teamsSize]);
  const players = useMemo(() => {
    return team?.players?.slice()?.sort((a, b) => {
      if (a.role === TournamentPlayerRole.Captain && b.role === TournamentPlayerRole.Player) {
        return -1;
      }
      if (a.role === TournamentPlayerRole.Player && b.role === TournamentPlayerRole.Captain) {
        return 1;
      }
      if (
        a?.status === TournamentPlayerStatus.Ready &&
        b?.status === TournamentPlayerStatus.Invited
      ) {
        return -1;
      }
      if (
        a?.status === TournamentPlayerStatus.Invited &&
        b?.status === TournamentPlayerStatus.Ready
      ) {
        return 1;
      }

      return a?.user?.userName < b.user?.userName ? -1 : 1;
    });
  }, [team.players]);

  const openConfirmDialog = useCallback(() => {
    dispatch(modalsReducers.openModal({ name: DialogName.Confirmation, data: team }));
  }, [dispatch, team]);

  const actions = useMemo(
    () => [
      {
        label: 'Disqualify',
        handler: openConfirmDialog,
      },
    ],
    [openConfirmDialog]
  );

  if (!team) {
    return null;
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box flex={1}>
          <TournamentTeamCardHeader team={team} />
        </Box>
        <ManageTournamentTeamActions team={team} actions={actions} />
      </Box>
      {players.map((player) => (
        <Fragment key={player?.userId}>
          <Divider className={classes.divider} />
          <TournamentTeamPlayerCard player={player} pt={1} pb={1} pr={2} pl={2} />
        </Fragment>
      ))}
      {emptyPlayers.map((_, index) => (
        <Fragment key={index}>
          <Divider className={classes.divider} />
          <Box sx={{ opacity: 0.6 }}>
            <EmptyUserCard title="empty" pt={1} pb={1} pr={2} pl={2} />
          </Box>
        </Fragment>
      ))}
    </Box>
  );
};
